import { useCallback, useState, type SyntheticEvent } from 'react';

import { cn } from '../../lib/cn.js';

/**
 * A photo shown WHOLE: scaled to fit its box (never zoomed, never cropped), with
 * bars where the proportions differ.
 *
 * The box is the PARENT's: this fills it with `absolute inset-0`, so the parent must
 * be positioned (`relative`). Sizing the picture with `h-full` instead looked the
 * same for a landscape photo, but a percentage height inside a flex/grid item has no
 * definite height to resolve against — a portrait or square photo fell back to its
 * natural proportions, overflowed the box and was CUT OFF at the bottom.
 *
 * The bars are white or black, whichever the picture's own edge is closer to,
 * so a dark product shot sits on black and a white-background shot on white —
 * the bars read as part of the picture instead of a frame around it. The edge is
 * sampled once per load from a tiny canvas copy; a picture that cannot be read
 * (or has a transparent edge) falls back to white.
 */
export function FitImage({
  src,
  alt = '',
  className,
  imgClassName,
}: {
  src: string;
  alt?: string;
  /** Extra classes for the box (it already fills its positioned parent). */
  className?: string;
  imgClassName?: string;
}): JSX.Element {
  const [dark, setDark] = useState(false);

  const onLoad = useCallback((e: SyntheticEvent<HTMLImageElement>) => {
    try {
      const img = e.currentTarget;
      const N = 24;
      const canvas = document.createElement('canvas');
      canvas.width = N;
      canvas.height = N;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, N, N);
      const px = ctx.getImageData(0, 0, N, N).data;
      let sum = 0;
      let n = 0;
      for (let y = 0; y < N; y += 1) {
        for (let x = 0; x < N; x += 1) {
          // Only the outer ring: that is what the bars continue.
          if (x !== 0 && y !== 0 && x !== N - 1 && y !== N - 1) continue;
          const i = (y * N + x) * 4;
          const a = (px[i + 3] ?? 255) / 255;
          // A transparent pixel is "white paper".
          const lum = (0.2126 * (px[i] ?? 255) + 0.7152 * (px[i + 1] ?? 255) + 0.0722 * (px[i + 2] ?? 255)) / 255;
          sum += lum * a + (1 - a);
          n += 1;
        }
      }
      setDark(n > 0 && sum / n < 0.35);
    } catch {
      setDark(false);
    }
  }, []);

  return (
    <span className={cn('absolute inset-0 overflow-hidden', dark ? 'bg-black' : 'bg-white', className)}>
      <img
        src={src}
        alt={alt}
        onLoad={onLoad}
        className={cn('absolute inset-0 h-full w-full object-contain', imgClassName)}
      />
    </span>
  );
}
